import {
  getScriptFallbackDefinition,
  serializeScriptFallbacks,
  type ScriptFallbackKey,
} from "./script-fallbacks";

export type PdfFontKey =
  | "geist"
  | "geist-mono"
  | "ibm-plex-sans"
  | "inter"
  | "merriweather"
  | "mona-sans"
  | "montserrat"
  | "raleway"
  | "roboto"
  | "source-serif-4"
  | "ysabeau";

export type FontFile = {
  weight: number;
  /** Relative to public/fonts/{key}/, e.g. latin-400.woff2 or 400.woff2. */
  fileName: string;
  unicodeRange?: string;
};

type FontDefinition = {
  key: PdfFontKey;
  label: string;
  family: string;
  fallback: string;
  files: FontFile[];
};

export const DEFAULT_FONT_KEY: PdfFontKey = "inter";

/**
 * Default monospace face for `code` / `pre` / KaTeX errors in print documents.
 * Embedded when selected (also available as a PdfFontKey for body/diagram).
 */
export const CODE_FONT_KEY: PdfFontKey = "geist-mono";
export const CODE_FONT_DIR = CODE_FONT_KEY;
export const CODE_FONT_FAMILY = "Geist Mono";
export const CODE_FONT_FALLBACK =
  'ui-monospace, SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace';

export function getCodeFontStack(
  key: PdfFontKey = CODE_FONT_KEY,
  neededFallbacks: readonly ScriptFallbackKey[] = [],
): string {
  return getFontStack(key, neededFallbacks);
}

/** Subsets always copied/embedded for the selected design font when present. */
export const PRIMARY_FONT_SUBSETS = [
  "latin",
  "latin-ext",
  "cyrillic",
  "greek",
] as const;

export type PrimaryFontSubset = (typeof PRIMARY_FONT_SUBSETS)[number];

/** Ordered roughly by familiarity for document UIs (dropdown display order). */
export const SUPPORTED_FONTS: FontDefinition[] = [
  {
    key: "inter",
    label: "Inter",
    family: "Inter",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "roboto",
    label: "Roboto",
    family: "Roboto",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "montserrat",
    label: "Montserrat",
    family: "Montserrat",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "merriweather",
    label: "Merriweather",
    family: "Merriweather",
    fallback: "Georgia, serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "source-serif-4",
    label: "Source Serif 4",
    family: "Source Serif 4",
    fallback: "Georgia, serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "raleway",
    label: "Raleway",
    family: "Raleway",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "ibm-plex-sans",
    label: "IBM Plex Sans",
    family: "IBM Plex Sans",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "geist",
    label: "Geist Sans",
    family: "Geist",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "geist-mono",
    label: "Geist Mono",
    family: CODE_FONT_FAMILY,
    fallback: CODE_FONT_FALLBACK,
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "mona-sans",
    label: "Mona Sans",
    family: "Mona Sans",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
  {
    key: "ysabeau",
    label: "Ysabeau",
    family: "Ysabeau",
    fallback: "system-ui, sans-serif",
    files: [
      { weight: 400, fileName: "latin-400.woff2" },
      { weight: 700, fileName: "latin-700.woff2" },
    ],
  },
];

export function getFontDefinition(key: PdfFontKey): FontDefinition {
  const font = SUPPORTED_FONTS.find((font) => font.key === key);

  if (!font) {
    throw new Error(`Unsupported PDF font key: ${key}.`);
  }

  return font;
}

export function isSupportedFontKey(value: unknown): value is PdfFontKey {
  return (
    typeof value === "string" &&
    SUPPORTED_FONTS.some((font) => font.key === value)
  );
}

export function getFontFamily(key: PdfFontKey): string {
  return getFontDefinition(key).family;
}

/**
 * CSS font-family stack: selected design face, then only needed Noto script
 * fallbacks, then the generic stack from the design font.
 */
export function getFontStack(
  key: PdfFontKey,
  neededFallbacks: readonly ScriptFallbackKey[] = [],
): string {
  const font = getFontDefinition(key);
  const scriptFamilies = neededFallbacks.map(
    (scriptKey) => `"${getScriptFallbackDefinition(scriptKey).family}"`,
  );

  return [`"${font.family}"`, ...scriptFamilies, font.fallback].join(", ");
}

/** Latin faces listed on the definition (subset-aware listing lives in font-embed). */
export function getFontFiles(key: PdfFontKey): FontFile[] {
  return getFontDefinition(key).files;
}

export function buildFontsCssHref(
  fontKey: PdfFontKey,
  {
    embed = false,
    scripts = [],
    diagramFontFamily,
    codeFontFamily,
  }: {
    embed?: boolean;
    scripts?: readonly ScriptFallbackKey[];
    diagramFontFamily?: PdfFontKey;
    codeFontFamily?: PdfFontKey;
  } = {},
): string {
  const params = new URLSearchParams({ family: fontKey });

  if (embed) {
    params.set("embed", "1");
  }

  if (diagramFontFamily && diagramFontFamily !== fontKey) {
    params.set("diagram", diagramFontFamily);
  }

  if (
    codeFontFamily &&
    codeFontFamily !== fontKey &&
    codeFontFamily !== diagramFontFamily
  ) {
    params.set("code", codeFontFamily);
  }

  if (scripts.length > 0) {
    params.set("scripts", serializeScriptFallbacks(scripts));
  }

  return `/api/fonts.css?${params.toString()}`;
}
